/**
 * THE HAND-OFF ROW — pure, no React, no DOM, no imports.
 *
 * Brian, on the live Hand off stage at phone width, 2026-09-11:
 * *"That's the shit I see in the portal. Useless."*
 *
 * ⚠️ WHAT MADE IT USELESS WAS NOT THE LAYOUT. The layout was broken too — the
 * row was a two-column grid in which `.buildDate` declared `grid-row: 1 / span
 * 2` and NO column, so auto-placement put it in an implicit track on top of the
 * title, and the status pill landed mid-sentence in the meta line, splitting the
 * word "generate". That is a real bug and it is fixed in the CSS.
 *
 * But the reason a rep gets nothing from this screen is that **the Artwork
 * Studio is a visual tool and its history had no pictures.** Eight builds, no
 * thumbnails, and five of the eight reading `0 images`. A rep opens Hand off to
 * see the piece they made and is handed a text list.
 *
 * ⚠️ AND `0 images` WAS A LIE OF OMISSION. Those builds did not fail. The
 * Builder's own contract says so in as many words — `renderPending: true` IS A
 * SUCCESS: the build record is written and every view's prompt is assembled,
 * and only the image call is skipped because `OPENAI_API_KEY` lives on Railway.
 * Rendering that as a bare `0` invites exactly one conclusion — *my build is
 * broken* — which is the opposite of what happened. A number cannot say "waiting
 * on the server"; a sentence can.
 *
 * So this module answers two questions about an index row, and it answers them
 * from the row's own fields ONLY. It never guesses:
 *
 *   imageStateOf(row)  what happened to the pictures, as a sentence
 *   thumbOf(row)       a render to show, if the row already carries one
 *
 * ⚠️ `pending` IS NOT THE DEFAULT. A row that says nothing about why it has no
 * images gets `unknown`, whose line promises nothing and tells the rep to open
 * the build. Defaulting to "waiting on the server" would be the same class of
 * error in the other direction: telling somebody their failed build is fine.
 */

/* ------------------------------------------------------------------ *
 * Thumbnails
 * ------------------------------------------------------------------ */

/**
 * Candidate keys for a render the INDEX row already carries.
 *
 * The index (`GET /api/artwork/builds`) is a bounded summary and its exact DTO
 * is the backend's (hard rule #4, and it is not modified from here). It may or
 * may not carry a preview. So this reads a candidate list the way
 * `lib/schools.js#normalizeSchool` does rather than coupling to one spelling —
 * when the backend starts shipping a preview under any of these names, the row
 * shows it with no change here.
 */
const THUMB_KEYS = [
  'thumbUrl', 'thumbnailUrl', 'thumb', 'thumbnail',
  'previewUrl', 'preview', 'heroUrl', 'hero', 'imageUrl',
]

/** Only a real http(s) URL is ever shown. Never `javascript:` / `data:` / a relative guess. */
function httpUrl(v) {
  const s = typeof v === 'string' ? v.trim() : ''
  if (!s) return ''
  return /^https?:\/\//i.test(s) ? s : ''
}

/**
 * A render to show for this row, or '' when the row carries none.
 *
 * Order matters: an OWNED, SR-hosted file wins over whatever else is on the
 * record. `hostedUrl` is the File Manager copy the render pipeline writes; `url`
 * may be a generator-side link that expires. Showing the expiring one when an
 * owned one exists is how a history grows dead images.
 */
export function thumbOf(row) {
  if (!row || typeof row !== 'object') return ''

  for (const k of THUMB_KEYS) {
    const hit = httpUrl(row[k])
    if (hit) return hit
  }

  const images = Array.isArray(row.images) ? row.images : []
  for (const img of images) {
    if (!img || typeof img !== 'object') continue
    const hit = httpUrl(img.hostedUrl)
    if (hit) return hit
  }
  for (const img of images) {
    if (!img || typeof img !== 'object') continue
    const hit = httpUrl(img.url)
    if (hit) return hit
  }
  return ''
}

/* ------------------------------------------------------------------ *
 * What happened to the pictures
 * ------------------------------------------------------------------ */

/** How many images this row actually claims. A non-number is 0, never NaN on screen. */
export function imageCountOf(row) {
  const n = Number(row?.imageCount)
  if (Number.isFinite(n) && n > 0) return Math.floor(n)
  const images = Array.isArray(row?.images) ? row.images : []
  return images.filter((i) => httpUrl(i?.hostedUrl) || httpUrl(i?.url)).length
}

/** Did the row tell us, in its own fields, that the render is still pending? */
function saysPending(row) {
  if (row?.renderPending === true) return true
  if (row?.renderPending === false) return false
  const s = String(row?.renderState || row?.imageState || '').toLowerCase()
  return s === 'pending' || s === 'queued' || s === 'deferred'
}

/** Did the row tell us it FAILED? A failure must never read as "waiting". */
function saysFailed(row) {
  const s = String(row?.renderState || row?.imageState || '').toLowerCase()
  if (s === 'failed' || s === 'error') return true
  const images = Array.isArray(row?.images) ? row.images : []
  return images.length > 0 && images.every((i) => i?.error)
}

/**
 * THE SENTENCE. `{ kind, count, line, tone }`.
 *
 * `kind` is for the component; `line` is what a rep reads. Every line is written
 * for somebody who does not know what a beast or a Railway env var is.
 */
export function imageStateOf(row) {
  const count = imageCountOf(row)

  if (count > 0) {
    return {
      kind: 'images',
      count,
      tone: 'ok',
      line: `${count} render${count === 1 ? '' : 's'}`,
    }
  }

  if (saysFailed(row)) {
    return {
      kind: 'failed',
      count: 0,
      tone: 'warn',
      line: 'The images failed to render. The build and its prompts are saved — open to see why.',
    }
  }

  if (saysPending(row)) {
    return {
      kind: 'pending',
      count: 0,
      tone: 'wait',
      /* The Builder's contract, in words a rep can act on: the build IS
       * recorded, the prompts ARE assembled, the pictures come later. */
      line: 'Saved — prompts assembled, images not generated yet. Open to read them.',
    }
  }

  return {
    kind: 'unknown',
    count: 0,
    /* Deliberately promises nothing. We were not told why there are no images,
     * so we do not invent a reason in either direction. */
    tone: 'wait',
    line: 'No renders on this build yet. Open it to see what it is waiting on.',
  }
}

/* ------------------------------------------------------------------ *
 * The meta line
 * ------------------------------------------------------------------ */

/**
 * The row's meta as a LIST OF WHOLE FACTS, never a pre-joined string.
 *
 * ⚠️ THIS IS THE FIX FOR THE WORD-PER-LINE WRAP. The old row built one string —
 * `` `${style} ${product} · ${mode} · ${count} images` `` — and dropped it into
 * a grid cell that auto-placement had squeezed to a few characters wide, so it
 * broke to one word per line: `championship` / `ring` / `.` / `ge` / `.` / `0` /
 * `images`. A single string cannot wrap well in a narrow box; a list of short
 * items can, because each item stays whole and the flex row wraps BETWEEN them.
 *
 * Empty values are OMITTED rather than rendered as a stray separator — the same
 * rule `buildPayload.js` applies to the payload itself.
 */
export function metaOf(row) {
  const out = []
  const push = (v) => {
    const s = typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim()
    if (s) out.push(s)
  }
  /* Style and product read as one fact — "championship ring" — because that is
   * how somebody says it out loud, and splitting them gains nothing. */
  push([row?.style, row?.product].map((v) => String(v || '').trim()).filter(Boolean).join(' '))
  push(row?.mode)
  return out
}

/** A date a human can read, and '' — never "Invalid Date" — when there isn't one. */
export function whenOf(row, locale) {
  const raw = row?.createdAt
  if (raw == null || raw === '') return ''
  const d = new Date(typeof raw === 'number' ? raw : String(raw))
  if (Number.isNaN(d.getTime())) return ''
  try {
    return d.toLocaleDateString(locale || undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return d.toDateString()
  }
}

/**
 * How many rows we will hydrate to find a thumbnail the index did not carry.
 *
 * BOUNDED ON PURPOSE. Opening 50 records to decorate a list is a scan, and the
 * whole reason the index exists is that the list must not scan the store. Rows
 * past the cap keep their placeholder and open normally.
 */
export const THUMB_HYDRATE_CAP = 12

/** The rows worth hydrating: they claim images, and carry no preview yet. */
export function rowsNeedingThumb(rows, cap = THUMB_HYDRATE_CAP) {
  const list = Array.isArray(rows) ? rows : []
  const want = []
  for (const r of list) {
    if (want.length >= cap) break
    if (imageCountOf(r) > 0 && !thumbOf(r)) want.push(r.buildId)
  }
  return want
}
